import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { paymentService, PaymentItem, GetPaymentsParams } from '../../services/paymentService';

interface PaymentState {
  payments: PaymentItem[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

const initialState: PaymentState = {
  payments: [],
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  itemsPerPage: 10,
};

// Async thunk to fetch payments
export const fetchPayments = createAsyncThunk(
  'payments/fetchPayments',
  async (params: GetPaymentsParams, { rejectWithValue }) => {
    try {
      const response = await paymentService.getPayments(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch payments');
    }
  }
);

const paymentSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setItemsPerPage: (state, action) => {
      state.itemsPerPage = action.payload;
      state.currentPage = 1; // Reset to first page when changing items per page
    },
    resetPayments: (state) => {
      state.payments = [];
      state.currentPage = 1;
      state.totalPages = 1;
      state.totalItems = 0;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = action.payload.data;
        state.currentPage = action.payload.pagination.page;
        state.itemsPerPage = action.payload.pagination.limit;
        state.totalItems = action.payload.pagination.total;
        state.totalPages = action.payload.pagination.pages;
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.payments = [];
      });
  },
});

export const { clearError, setCurrentPage, setItemsPerPage, resetPayments } = paymentSlice.actions;
export default paymentSlice.reducer;